import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import profileLoaderModule from './userProfileLoader.cjs';

const { createUserProfileLoader } = profileLoaderModule;

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const profileLoaderRef = useRef(null)

  if (!profileLoaderRef.current) {
    profileLoaderRef.current = createUserProfileLoader({
      supabase,
      onProfile: setUserProfile,
      onLoading: setProfileLoading,
      onError: (error) => console.error('Profile load error:', error?.message || error)
    });
  }

  // ✅ REQUIRED: Separate async operations object
  const profileOperations = {
    load: profileLoaderRef.current.load,
    
    clear() {
      profileLoaderRef.current.clear()
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // ✅ REQUIRED: Protected auth handlers
  const authStateHandlers = {
    // CRITICAL: This MUST remain synchronous
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        try {
          localStorage?.setItem('mlbp:lastUserEmail', session?.user?.email || '');
        } catch (storageError) {
          console.warn('No se pudo recordar el correo del usuario:', storageError?.message);
        }
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        try {
          localStorage?.removeItem('mlbp:lastUserEmail');
        } catch (storageError) {
          console.warn('No se pudo limpiar el correo recordado del usuario:', storageError?.message);
        }
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // PROTECTED: Never modify this callback signature
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  const signUp = async (email, password, userData = {}) => {
    try {
      console.log('Intentando registrar usuario en Supabase:', { email, password, userData });
      // Registrar usuario en Supabase Auth, el trigger creará el registro en user_profiles
      const redirectTo = (typeof window !== 'undefined' && window?.location?.origin)
        ? `${window.location.origin}/confirm-email`
        : 'https://mercadobulk.com/confirm-email';

      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectTo,
          data: {
            full_name: userData?.name || userData?.contactName || 'User',
            business_name: userData?.businessName || '',
            phone: userData?.phone || '',
            market: userData?.market || '',
            role: userData?.role ? userData.role : 'user'
          }
        }
      });
      console.log('Supabase signUp result:', { data, error });
      if (error) {
        console.error('Error al registrar usuario en Supabase:', error.message || error);
      }
      // El trigger en Supabase creará el registro en user_profiles automáticamente
      return { data, error };
    } catch (error) {
      console.error('Excepción en signUp:', error);
      return { data: null, error };
    }
  }

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signUp,
    signIn,
    signOut,
    updateProfile: profileOperations?.load
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}